import {
  calculateStackGeometry,
  calculateMeasuredWoodStack,
  calculateMeasuredChips,
  presentWoodStackResult,
  StackMeasurements,
  MeasuredWoodStackInput,
  MeasuredChipsInput,
  roundInventoryValue,
  WOOD_STACK_DISPLAY_PRECISION,
} from "@foresting-tomorrow/forest_tools_inventory";
import { FirestoreInventoryMeasurementProps } from "types/auth";

/** Form dimensions have no thousands separators. Blank is missing, not zero. */
export function parseMeasurementNumber(value: string): number {
  return value.trim() === "" ? NaN : Number(value.replace(",", "."));
}

/** Height selectors use whole centimetres; normalize metre conversion noise on reload. */
export function heightMetresToInputCm(heightM: number): string {
  return Number.isFinite(heightM) && heightM >= 0 ? roundInventoryValue(heightM * 100, 0).toString() : "";
}

export type MeasurementForm = {
  numberOfSections: number | "";
  numberOfSectionsBack: number | "";
  frontHeights: string[];
  backHeights: string[];
  stackLengthFront: string;
  stackLengthBack: string;
  measureBothSides: boolean;
};

export function measurementsFromForm(form: MeasurementForm): StackMeasurements | null {
  const side = (length: string, heights: string[], count: number | "") => ({
    lengthM: parseMeasurementNumber(length),
    heightsM: Array.from({ length: Number(count) }, (_, i) => parseMeasurementNumber(heights[i] ?? "") / 100) as [number, ...number[]],
  });
  const measurements = {
    front: side(form.stackLengthFront, form.frontHeights, form.numberOfSections),
    ...(form.measureBothSides ? { back: side(form.stackLengthBack, form.backHeights, form.numberOfSectionsBack) } : {}),
  };
  try {
    calculateStackGeometry(measurements);
    return measurements;
  } catch {
    return null; // An unfinished form is not calculation-ready.
  }
}

/** Rows are a display layout only; matching row indexes never pair the observations. */
export function measurementRowsFromForm(form: Pick<MeasurementForm,
  "numberOfSections" | "numberOfSectionsBack" | "frontHeights" | "backHeights" | "measureBothSides"
>) {
  const front = form.frontHeights.slice(0, Number(form.numberOfSections));
  const back = form.measureBothSides ? form.backHeights.slice(0, Number(form.numberOfSectionsBack)) : [];
  const height = (value: string | undefined) => {
    const parsed = parseMeasurementNumber(value ?? "");
    return Number.isFinite(parsed) && parsed >= 0 ? parsed / 100 : null;
  };
  return Array.from({ length: Math.max(front.length, back.length) }, (_, i) => ({
    section: i + 1,
    frontHeightM: height(front[i]),
    backHeightM: height(back[i]),
  }));
}

export function measurementsFromRecord(record: Pick<FirestoreInventoryMeasurementProps,
  "stackLengthFrontM" | "stackLengthBackM" | "frontHeightsM" | "backHeightsM"
>): StackMeasurements | null {
  const measurements = {
    front: { lengthM: record.stackLengthFrontM!, heightsM: record.frontHeightsM as [number, ...number[]] },
    ...(record.backHeightsM?.length ? {
      back: { lengthM: record.stackLengthBackM!, heightsM: record.backHeightsM as [number, ...number[]] },
    } : {}),
  };
  try { calculateStackGeometry(measurements); return measurements; } catch { return null; }
}

export function woodStackCalculation(input: MeasuredWoodStackInput | null) {
  if (!input) return null;
  try { return calculateMeasuredWoodStack(input); } catch { return null; }
}

export function chipsCalculation(input: MeasuredChipsInput | null) {
  if (!input) return null;
  try { return calculateMeasuredChips(input); } catch { return null; }
}

/** Persist observed readings only, trimming independently for each side. */
export function heightsForSave(values: string[], count: number | ""): number[] | null {
  const heights = values.slice(0, Number(count)).filter(value => value.trim() !== "").map(value => parseMeasurementNumber(value) / 100);
  if (heights.some(value => !Number.isFinite(value) || value < 0)) throw new Error("Invalid height measurement");
  return heights.length ? heights : null;
}

export function woodStackFromRecord(record: FirestoreInventoryMeasurementProps) {
  const measurements = measurementsFromRecord(record);
  return measurements ? woodStackCalculation({
    measurements,
    species: record.species?.map(s => ({ species: s.id, mix: s.mix })) ?? [],
    averageDiameterCm: (record.averageDiameterM ?? 0) * 100,
    logAverageLengthM: record.logLengthM ?? 0,
    numberOfSupportLogs: record.numberOfSupportLogs ?? 0,
    numberOfCullLogs: record.numberOfCullLogs ?? 0,
    volumeOfSupportLogsM3: record.volumeOfSupportLogsM3 ?? 0,
    volumeOfCullLogsM3: record.volumeOfCullLogsM3 ?? 0,
    compensationFactors: record.stackingParameters ?? {},
  }) : null;
}

export function displayInventoryValue(value: number, quantity: keyof typeof WOOD_STACK_DISPLAY_PRECISION) {
  return roundInventoryValue(value, WOOD_STACK_DISPLAY_PRECISION[quantity]);
}

export { presentWoodStackResult };
